import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { Download, FileText, Image as ImageIcon, Package, Paperclip, Link2 } from "lucide-react"
import { api } from "@/lib/api"
import { AttachHeroModal } from "./AttachHeroModal"
import { wegSchritt } from "@/lib/wege"

/**
 * Anhänge einer E-Mail. Lazy-load: Klick auf "Anhänge laden" →
 * holt aus DB-Cache ODER IMAP (dauert etwas).
 * Plus: "An HERO-Projekt anhängen"-Button pro Attachment.
 */
export function AttachmentsSection({ emailId, tenantSlug, titel, hatteAnhaenge }: {
  emailId: number
  tenantSlug?: string
  /** Ueberschrift, die NUR erscheint, wenn es auch etwas zu zeigen gibt.
   *
   *  Die Komponente rendert sich bei null Anhaengen bewusst weg — steht die
   *  Ueberschrift beim Aufrufer, bleibt sie als leere Zeile stehen. Genau das
   *  passierte im E-Mail-Popup (13.08.2026): „ANHÄNGE" mit nichts darunter,
   *  weil `has_attachments` gesetzt war, die Mail aber nur eingebettete
   *  Bilder trug. */
  titel?: React.ReactNode
  /** Hatte die Mail laut Datenbank Anhänge? Nur dann ist es eine Nachricht
   *  wert, wenn sich keine laden lassen (#54) — sonst stünde bei jeder alten,
   *  längst gelöschten Mail ohne Anhang ein Hinweis. */
  hatteAnhaenge?: boolean
}) {
  /** Die Anhaenge, die gerade in einen Vorgang sollen. Ein Element beim Klick
   *  auf eine Zeile, mehrere ueber die Haekchen (#42). */
  const [attachHero, setAttachHero] = useState<
    { id: number; filename: string; size: number }[] | null
  >(null)
  /** Mehrfachauswahl. Marlene am 26.08.2026: „es wäre gut, wenn man durch
   *  Häkchen alle gesammelt dann hochladen könnte ins Projekt" — bei fünf
   *  Fotos war es fünfmal derselbe Dialog samt Projektsuche. */
  const [markiert, setMarkiert] = useState<Set<number>>(new Set())
  // Auto-Load: sobald die Mail geoeffnet wird, Anhaenge direkt fetchen (aus DB-Cache
  // oder via IMAP-Fallback im Backend). Kein manueller Klick mehr noetig.
  const { data, isLoading, error } = useQuery({
    queryKey: ["attachments", emailId],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/emails/{email_id}/attachments", {
        params: { path: { email_id: emailId } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 60,
  })

  if (isLoading) {
    return (
      <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <Paperclip className="w-3.5 h-3.5" /> Lädt Anhänge…
      </div>
    )
  }
  if (error) {
    return <div className="mt-3 text-[12px] text-destructive">Anhänge: {error instanceof Error ? error.message : "unbekannt"}</div>
  }
  // Eingebettete Bilder gehoeren nicht in die Anhang-Liste — sie stehen schon
  // im Mailtext. Eine Outlook-Signatur bringt fuenf davon mit; zusammen mit
  // dem Zitat-Verlauf standen vor dem einen echten PDF regelmaessig 30 Zeilen
  // „image001.png" (gemeldet 12.08.2026: „man sieht die Anhaenge gar nicht
  // mehr"). Weitergeleitet werden sie weiterhin — die Liste hier ist Anzeige.
  const sichtbar = (data?.attachments || []).filter((a) => !a.is_inline)
  if (!sichtbar.length) {
    // 🔴 #54 (Paul, 27.08.2026): „anhänge werden nicht mehr geladen?" — hier
    // stand nichts, weil hier IMMER nichts stand. Ob die Mail keine Anhänge
    // hatte oder ob sie nicht mehr im Postfach liegt (die gemeldete lag in
    // keinem der 482 Ordner), sah identisch aus: ein leerer Bereich.
    // Sagt das Backend, dass es die Mail nicht finden konnte, und hatte sie
    // laut Datenbank Anhänge, dann gehört das dem Nutzer gesagt.
    const hinweis = data?.hinweis
    if (hatteAnhaenge && hinweis) {
      return (
        <div className="mt-3 inline-flex items-start gap-1.5 text-[12px] text-amber-700 dark:text-amber-400">
          <Paperclip className="w-3.5 h-3.5 mt-[1px] shrink-0" />
          <span>{hinweis}</span>
        </div>
      )
    }
    // Sonst: gar nichts rendern (Flag war evtl. falsch-positiv).
    return null
  }

  return (
    <div className="mt-3 space-y-1.5">
      {titel}
      {markiert.size > 0 && tenantSlug && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary/5 border border-primary/20 text-[12.5px]">
          <button
            type="button"
            onClick={() => setMarkiert(new Set())}
            className="font-semibold"
            title="Auswahl aufheben"
          >{markiert.size} ausgewählt ✕</button>
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
            onClick={() => {
              wegSchritt("anhaenge_gesammelt_an_projekt")
              setAttachHero(
                sichtbar
                  .filter((a) => markiert.has(a.id))
                  .map((a) => ({ id: a.id, filename: a.filename, size: a.size })),
              )
            }}
          >
            <Link2 className="w-3.5 h-3.5" /> Gesammelt an HERO-Projekt
          </button>
        </div>
      )}
      {sichtbar.map((a) => (
        <div
          key={a.id}
          className="flex items-center gap-2 px-3 py-2 rounded-md border bg-card hover:border-primary hover:shadow-sm text-[13px]"
        >
          {tenantSlug && (
            <input
              type="checkbox"
              className="shrink-0"
              aria-label={`${a.filename} auswählen`}
              checked={markiert.has(a.id)}
              onChange={() => setMarkiert((s) => {
                const n = new Set(s)
                n.has(a.id) ? n.delete(a.id) : n.add(a.id)
                return n
              })}
            />
          )}
          <AttachmentIcon a={a} />
          <a
            href={`/api/v1/emails/attachments/${a.id}/download`}
            target="_blank"
            rel="noopener"
            className="flex-1 min-w-0 truncate no-underline text-foreground"
          >
            {a.filename}
          </a>
          <span className="text-[11px] text-muted-foreground shrink-0">{fmtSize(a.size)}</span>
          {tenantSlug && (
            <button
              type="button"
              onClick={() => {
                // 🔑 Genau dieser Schritt gab den Anlass fuer #44: fuenfmal
                // hintereinander derselbe Dialog. In der Abfolge faellt das auf.
                wegSchritt("anhang_einzeln_an_projekt")
                setAttachHero([{ id: a.id, filename: a.filename, size: a.size }])
              }}
              className="shrink-0 inline-flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-primary px-1.5 py-0.5 rounded"
              title="An HERO-Projekt anhängen"
            >
              <Link2 className="w-3.5 h-3.5" /> HERO
            </button>
          )}
          <a
            href={`/api/v1/emails/attachments/${a.id}/download`}
            target="_blank"
            rel="noopener"
            className="shrink-0 text-muted-foreground hover:text-primary"
            title="Herunterladen"
          >
            <Download className="w-3.5 h-3.5" />
          </a>
        </div>
      ))}
      {tenantSlug && attachHero && (
        <AttachHeroModal
          open={!!attachHero}
          onClose={() => { setAttachHero(null); setMarkiert(new Set()) }}
          tenantSlug={tenantSlug}
          emailId={emailId}
          anhaenge={attachHero}
        />
      )}
    </div>
  )
}

function AttachmentIcon({ a }: { a: { is_image: boolean; is_pdf: boolean; is_archive: boolean } }) {
  const cls = "w-4 h-4 shrink-0"
  if (a.is_image) return <ImageIcon className={cls + " text-blue-600"} />
  if (a.is_pdf) return <FileText className={cls + " text-red-600"} />
  if (a.is_archive) return <Package className={cls + " text-amber-600"} />
  return <FileText className={cls + " text-muted-foreground"} />
}

function fmtSize(bytes: number): string {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
